import { getAxiosClient } from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse, FailApiResponse } from '../../responses';
import type { FiscalYear, JournalEntry } from '@/api/models';
import { JournalEndpoints } from '../journal/journalEndpoints';
import { mapJournalEntry } from '../journal/journalMapping';
import type { IPeriodsService } from './IPeriodsService';
import { PeriodsEndpoints } from './periodsEndpoints';
import {
    mapClosePreview,
    mapFiscalYear,
    mapFiscalYears,
    mapPeriods,
    parseFiscalYearId,
    parsePeriodId,
    summarisePeriodEntries,
    type ClosingEntryPreviewWire,
    type FiscalYearWire,
    type PeriodEntryWire,
} from './periodsMapping';
import type {
    CloseFiscalYearResponse,
    ClosePreviewResponse,
    GetPeriodStatusResponse,
    ListFiscalYearsResponse,
    ListPeriodsResponse,
    PeriodWriteResponse,
    ReopenFiscalYearResponse,
    RolloverResponse,
} from './responses';

type PeriodsServiceOptions = {
    baseUrl: string;
    /** Overridable for tests; the server caps `PageSize` at 200. */
    pageSize?: number;
};

/** The server's own ceiling — asking for more silently yields 200. */
const MAX_PAGE_SIZE = 200;

/**
 * A local refusal, in the same envelope shape the HTTP layer produces.
 *
 * `FailApiResponse` does not mention `T`, so one of these satisfies any
 * `ApiResponse<T>` — the call site keeps its return type without a cast.
 */
const refuse = (message: string): FailApiResponse => ({
    success: false,
    error: {
        isAxiosError: false,
        status: 400,
        message,
        name: '400',
    },
});

/** Refusal used when an id cannot address anything, before any HTTP happens. */
const badId = (what: string): FailApiResponse =>
    refuse(`Malformed ${what} identifier`);

/**
 * Periods, fiscal years and the year-end close, against the live bos-api.
 *
 * ## What this service has to assemble
 *
 * The endpoints are thinner than the screen. Three domain shapes have no single
 * call behind them and are composed here:
 *
 * - **`listPeriods`** — the API serves no period list and no per-period counts
 *   or totals. The periods come nested on the year; the counts come from a
 *   journal read over the year's range, bucketed by each period's own bounds.
 * - **`closeFiscalYear`** — the close returns the year, not the entry it just
 *   posted. The `CLOSING` entry is fetched by the `closingEntryId` that comes
 *   back, so the result can link into the audit trail.
 * - **`reopenFiscalYear`** — the reopen returns the year with both entry ids
 *   **cleared**, so the reversals it posted are unnameable from its own
 *   response. The ids are read *before* the reopen and resolved to their
 *   reversals afterwards through `reversedByEntryId`.
 *
 * ## What it refuses to invent
 *
 * A composed read whose supporting call fails degrades to the part that
 * succeeded rather than to a fabricated whole: a year whose journal read fails
 * still lists its periods, with counts left at zero, because a wrong count is
 * worse than an absent one on a screen whose purpose is to say what a month
 * holds before you seal it. A *write* whose follow-up read fails still reports
 * success — the entry was posted; only the convenience link is missing.
 *
 * ## No `source`, ever
 *
 * Every mutating call posts **no body**. `CLOSING` and `ROLLOVER` are
 * server-assigned and rejected if supplied; sending nothing makes that
 * structural rather than a rule someone has to remember.
 */
export class PeriodsService implements IPeriodsService {
    private readonly baseUrl: string;
    private readonly pageSize: number;

    constructor({ baseUrl, pageSize = MAX_PAGE_SIZE }: PeriodsServiceOptions) {
        this.baseUrl = baseUrl;
        this.pageSize = Math.min(pageSize, MAX_PAGE_SIZE);
    }

    private get client() {
        return getAxiosClient({ baseURL: this.baseUrl });
    }

    // ── reads ───────────────────────────────────────────────────────────────

    private fetchYears(): Promise<ApiResponse<unknown>> {
        return makeApiRequest<unknown>(
            this.client.get(PeriodsEndpoints.fiscalYears()),
        );
    }

    private fetchYear(year: number): Promise<ApiResponse<FiscalYearWire>> {
        return makeApiRequest<FiscalYearWire>(
            this.client.get(PeriodsEndpoints.fiscalYear(year)),
        );
    }

    /**
     * Postability of a single date — the Journal Entry form's gate.
     *
     * There is **no `/periods/status` endpoint**; the verdict is resolved from
     * the fiscal-year read. A `LOCKED` period reports `CLOSED` because the
     * effect on posting is identical and `PeriodState` carries only the two
     * values the gate needs.
     *
     * **Failing open is deliberate.** If the read fails, or the date falls in no
     * fiscal year at all, this reports `OPEN` and lets the server refuse.
     * Reporting `CLOSED` on a network blip would block posting to a perfectly
     * open period for a reason the user cannot act on; reporting `OPEN` risks an
     * optimistic Post that the server rejects with its own clear message — which
     * the entry form already handles.
     */
    public async getPeriodStatus(
        date: string,
    ): Promise<ApiResponse<GetPeriodStatusResponse>> {
        const open: GetPeriodStatusResponse = {
            date,
            state: 'OPEN',
            periodLabel: date.slice(0, 7),
        };
        const result = await this.fetchYears();
        if (!result.success) {
            return { success: true, response: open };
        }

        const covers = (start: string, end: string): boolean =>
            Boolean(start) && Boolean(end) && start <= date && date <= end;

        const year = mapFiscalYears(result.response).find((candidate) =>
            covers(candidate.startDate, candidate.endDate),
        );
        if (!year) {
            return { success: true, response: open };
        }

        const wire = (result.response as FiscalYearWire[]).find(
            (candidate) => candidate.year === year.year,
        );
        const period = mapPeriods(wire ?? {}).find((candidate) =>
            covers(candidate.startDate, candidate.endDate),
        );
        const closed =
            period?.status === 'CLOSED' || period?.status === 'LOCKED';

        return {
            success: true,
            response: {
                date,
                state: closed ? 'CLOSED' : 'OPEN',
                periodLabel: period?.yearMonth ?? open.periodLabel,
            },
        };
    }

    public async listFiscalYears(): Promise<
        ApiResponse<ListFiscalYearsResponse>
    > {
        const result = await this.fetchYears();
        return result.success
            ? {
                  success: true,
                  response: { items: mapFiscalYears(result.response) },
              }
            : result;
    }

    /**
     * One year's months, with what each contains.
     *
     * `fiscalYearId` is the year as a string (see `models/Periods`), because
     * that is what the path takes.
     */
    public async listPeriods(
        fiscalYearId: string,
    ): Promise<ApiResponse<ListPeriodsResponse>> {
        const year = parseFiscalYearId(fiscalYearId);
        if (year === null) {
            return badId('fiscal year');
        }

        const result = await this.fetchYear(year);
        if (!result.success) {
            return result;
        }
        const fiscalYear = mapFiscalYear(result.response);
        if (!fiscalYear) {
            return badId('fiscal year');
        }

        const periods = mapPeriods(result.response);
        const entries = await this.fetchYearEntries(fiscalYear);

        return {
            success: true,
            response: {
                fiscalYear,
                items: entries
                    ? summarisePeriodEntries(periods, entries)
                    : periods,
            },
        };
    }

    /**
     * Every journal entry dated inside the year, for the per-period counts.
     *
     * Paged because the server caps `PageSize` at 200 and a real year exceeds
     * that. Returns `null` — not an empty list — when the read fails, so the
     * caller can leave the counts unknown rather than render a confident zero.
     */
    private async fetchYearEntries(
        fiscalYear: FiscalYear,
    ): Promise<PeriodEntryWire[] | null> {
        const collected: PeriodEntryWire[] = [];
        let page = 1;

        // Bounded: the loop exits on the server's own `totalPages`, and the
        // guard stops a malformed response from spinning.
        for (let guard = 0; guard < 50; guard += 1) {
            const result = await makeApiRequest<{
                items?: PeriodEntryWire[];
                totalPages?: number;
            }>(
                this.client.get(
                    JournalEndpoints.list({
                        page,
                        pageSize: this.pageSize,
                        fromDate: fiscalYear.startDate,
                        toDate: fiscalYear.endDate,
                    }),
                ),
            );
            if (!result.success) {
                return null;
            }
            collected.push(...(result.response.items ?? []));
            const totalPages = result.response.totalPages ?? 1;
            if (page >= totalPages) {
                return collected;
            }
            page += 1;
        }
        return collected;
    }

    /**
     * Dry run of the year-end close.
     *
     * `locale` is measured to change the account names the server resolves, so
     * the preview reads in the user's language. Nothing is written.
     */
    public async previewFiscalYearClose(
        fiscalYearId: string,
        locale?: string,
    ): Promise<ApiResponse<ClosePreviewResponse>> {
        const year = parseFiscalYearId(fiscalYearId);
        if (year === null) {
            return badId('fiscal year');
        }
        const result = await makeApiRequest<ClosingEntryPreviewWire>(
            this.client.get(PeriodsEndpoints.closePreview(year), {
                params: locale ? { locale } : undefined,
            }),
        );
        if (!result.success) {
            return result;
        }
        const preview = mapClosePreview(result.response);
        // A preview with no lines is `NOTHING_TO_CLOSE` in all but name: there
        // is no entry to approve, so this refuses rather than offering an empty
        // dialog with a live Confirm button.
        return preview
            ? { success: true, response: preview }
            : refuse('The close preview returned no lines');
    }

    // ── writes ──────────────────────────────────────────────────────────────

    /**
     * Close one month.
     *
     * The server returns the **whole year**, so the changed period is picked out
     * of it rather than trusted from a separate shape. It also refuses when an
     * earlier period is still open — `periodsMeta` pre-empts that with a stated
     * reason, and this is what happens if the state moved underneath.
     */
    public closePeriod(
        periodId: string,
    ): Promise<ApiResponse<PeriodWriteResponse>> {
        return this.writePeriod(periodId, PeriodsEndpoints.closePeriod);
    }

    /** Reopen one closed month. */
    public reopenPeriod(
        periodId: string,
    ): Promise<ApiResponse<PeriodWriteResponse>> {
        return this.writePeriod(periodId, PeriodsEndpoints.reopenPeriod);
    }

    private async writePeriod(
        id: string,
        path: (year: number, periodNumber: number) => string,
    ): Promise<ApiResponse<PeriodWriteResponse>> {
        const parsed = parsePeriodId(id);
        if (!parsed) {
            return badId('period');
        }
        const result = await makeApiRequest<FiscalYearWire>(
            this.client.post(path(parsed.year, parsed.periodNumber)),
        );
        if (!result.success) {
            return result;
        }
        const fiscalYear = mapFiscalYear(result.response);
        const period = mapPeriods(result.response).find(
            (candidate) => candidate.sequence === parsed.periodNumber,
        );
        if (!fiscalYear || !period) {
            return badId('period');
        }
        return { success: true, response: { period, fiscalYear } };
    }

    /**
     * Post the year's CLOSING entry.
     *
     * **No body.** The endpoint accepts `{ date }` and honours it, which would
     * move the entry off the date the preview showed — so the commit is left to
     * the server's own default and matches what the user approved.
     */
    public async closeFiscalYear(
        fiscalYearId: string,
    ): Promise<ApiResponse<CloseFiscalYearResponse>> {
        const year = parseFiscalYearId(fiscalYearId);
        if (year === null) {
            return badId('fiscal year');
        }
        const result = await makeApiRequest<FiscalYearWire>(
            this.client.post(PeriodsEndpoints.close(year)),
        );
        if (!result.success) {
            return result;
        }
        const fiscalYear = mapFiscalYear(result.response);
        if (!fiscalYear) {
            return badId('fiscal year');
        }
        const closingEntry = await this.fetchEntry(fiscalYear.closingEntryId);
        return {
            success: true,
            response: { fiscalYear, closingEntry },
        };
    }

    /**
     * Post the ROLLOVER entry: current-year result → retained earnings.
     *
     * Implemented and verified live, but **not reachable from the screen**: the
     * API has no `/rollover/preview`, so there is no dry run to approve, and the
     * action stays behind `LedgerHandoffNotice` per the both-sides-must-be-real
     * rule. Kept here so reopening it is a one-line change the day a preview
     * endpoint lands.
     *
     * Two measured properties worth knowing: the entry is dated `{year+1}-01-01`
     * and numbered in that year's sequence, even when that fiscal year has not
     * been generated; and a second call is refused, so there is a real duplicate
     * guard.
     */
    public async postRetainedEarningsRollover(
        fiscalYearId: string,
    ): Promise<ApiResponse<RolloverResponse>> {
        const year = parseFiscalYearId(fiscalYearId);
        if (year === null) {
            return badId('fiscal year');
        }
        const result = await makeApiRequest<FiscalYearWire>(
            this.client.post(PeriodsEndpoints.rollover(year)),
        );
        if (!result.success) {
            return result;
        }
        const fiscalYear = mapFiscalYear(result.response);
        if (!fiscalYear) {
            return badId('fiscal year');
        }
        const rolloverEntry = await this.fetchEntry(fiscalYear.rolloverEntryId);
        return { success: true, response: { fiscalYear, rolloverEntry } };
    }

    /**
     * Reopen a closed year by reversing its system entries.
     *
     * The reopen response clears `closingEntryId` and `rolloverEntryId`, so the
     * entries it just reversed cannot be named from it. They are read from the
     * year **before** the call and resolved afterwards through each original's
     * `reversedByEntryId` — which is also what makes the reversal trail
     * navigable in both directions.
     *
     * Ordered newest-undo-first (rollover before closing) to match the order the
     * server posts them in.
     */
    public async reopenFiscalYear(
        fiscalYearId: string,
    ): Promise<ApiResponse<ReopenFiscalYearResponse>> {
        const year = parseFiscalYearId(fiscalYearId);
        if (year === null) {
            return badId('fiscal year');
        }

        // Read the ids first — the reopen response will not carry them.
        const before = await this.fetchYear(year);
        const originals = before.success
            ? [
                  before.response.rolloverEntryId,
                  before.response.closingEntryId,
              ].filter((id): id is string => Boolean(id))
            : [];

        const result = await makeApiRequest<FiscalYearWire>(
            this.client.post(PeriodsEndpoints.reopen(year)),
        );
        if (!result.success) {
            return result;
        }
        const fiscalYear = mapFiscalYear(result.response);
        if (!fiscalYear) {
            return badId('fiscal year');
        }

        const reversalEntries: JournalEntry[] = [];
        for (const originalId of originals) {
            const original = await this.fetchEntry(originalId);
            const reversal = await this.fetchEntry(
                original?.reversedByEntryId ?? null,
            );
            if (reversal) {
                reversalEntries.push(reversal);
            }
        }

        return { success: true, response: { fiscalYear, reversalEntries } };
    }

    /**
     * One entry, or `null`.
     *
     * Used only to decorate a write that has already succeeded, so a failure
     * here costs the convenience link and nothing else — never the report that
     * the entry was posted.
     */
    private async fetchEntry(id: string | null): Promise<JournalEntry | null> {
        if (!id) {
            return null;
        }
        const result = await makeApiRequest<unknown>(
            this.client.get(JournalEndpoints.get(id)),
        );
        return result.success ? mapJournalEntry(result.response) : null;
    }
}
